/**
 * OnePageCheckoutPage – demo2025 theme
 * Kombiniert Warenkorb, Lieferung und Abschluss auf einer Seite.
 */
import React, { Component, Fragment } from "react"
import { observer } from "mobx-react"
import { Link } from "react-router-dom"
import { Button, Icon, Message, Modal, Divider, Responsive } from "semantic-ui-react"
import { Col, Row, FormField, FormInput, Checkbox, Form } from "elemental-react16"
import shop from "shop/shop"
import track from "shop/track"
import moment from "moment"
import _ from "lodash"
import swal from "sweetalert"
import queryString from "qs"
import ProductTable from "components/Product/ProductTable"
import Price from "components/Price/Price"
import Spinner from "components/Spinner/Spinner"
import SuccessTable from "components/Product/SuccessTable"
import productStore from "stores/productStore"
import DeliveryDatePicker from "components/Date/DeliveryDatePicker"
import "style/CheckoutPage.scss"
import "style/DeliveryPage.scss"
import "style/OnePageCheckoutPage.scss"

window.moment = moment

// ── Wiederverwendbare UI-Bausteine ──────────────────────────────────────────

const SectionCard = ( { children, style = {} } ) => (
  <div className="opc-card" style={style}>
    {children}
  </div>
)

const StepBadge = ( { n, done } ) => (
  <div className={`opc-step-badge${done ? " opc-step-badge--done" : ""}`}>
    {done ? <Icon name="check"/> : n}
  </div>
)

const StepHeader = ( { number, title, done } ) => (
  <div className="opc-step-header">
    <StepBadge n={number} done={done}/>
    <h2 className="opc-step-header__title">{title}</h2>
  </div>
)

// ── Adressauswahl-Karte ─────────────────────────────────────────────────────

const AddressCard = ( { location, selected, onSelect } ) => (
  <div
    onClick={() => onSelect( location )}
    className={`opc-addr-card${selected ? " opc-addr-card--selected" : ""}`}
  >
    {selected && (
      <div className="opc-addr-card__check">
        <Icon name="check circle" color="blue"/>
      </div>
    )}
    <div className="opc-addr-card__name">{location.name1} {location.name2}</div>
    <div className="opc-addr-card__address">
      {location.street && <div>{location.street}</div>}
      {location.addition && <div>{location.addition}</div>}
      <div>{location.pcode} {location.city}</div>
      {location.extras && location.extras.ShipmentMethodCodeText && (
        <div className="opc-addr-card__shipmethod">{location.extras.ShipmentMethodCodeText}</div>
      )}
    </div>
  </div>
)

const AddressBox = ( { title, address, fixed, fallback, locations = [], locationSelected, onSelect } ) => {
  const [open, setOpen] = React.useState( false )

  const handleSelect = ( loc ) => {
    onSelect( loc )
  }

  return (
    <div className="opc-address-box">
      <div className="opc-address-box__label">
        {title}
      </div>

      {address ? (
        <div className="opc-address-box__content">
          <strong>
            {address.title
              || `${address.name1 || ""} ${address.name2 || ""}`.trim()
              || address.company}
          </strong>
          {address.street && <div>{address.street}</div>}
          {(address.pcode || address.city) && <div>{address.pcode} {address.city}</div>}
          {address.country && <div>{address.country}</div>}
          {address.email && <div className="opc-address-box__meta">{address.email}</div>}
          {address.phone && <div className="opc-address-box__meta">{address.phone}</div>}
        </div>
      ) : (
        <div className="opc-address-box__fallback">{fallback}</div>
      )}

      {!fixed && locations.length > 0 && (
        <Fragment>
          <div className="opc-address-box__edit-wrap">
            <Button basic size="mini" onClick={() => setOpen( true )}>
              <Icon name="edit outline"/> {translate( "ändern" )}
            </Button>
          </div>

          <Modal open={open} onClose={() => setOpen( false )} size="large">
            <Modal.Header>
              <div className="opc-modal-header">
                <span className="opc-modal-header__title">{translate( "Lieferadresse auswählen" )}</span>
                <Button basic circular icon="times" onClick={() => setOpen( false )}/>
              </div>
            </Modal.Header>
            <Modal.Content>
              {/* Option: Rechnungsadresse (Standard) */}
              <div
                onClick={() => onSelect( null )}
                className={`opc-billing-option${!locationSelected ? " opc-billing-option--selected" : ""}`}
              >
                {!locationSelected && <Icon name="check circle" color="blue"/>}
                <div>
                  <div className="opc-billing-option__title">{translate( "Rechnungsadresse verwenden" )}</div>
                  <div className="opc-billing-option__sub">{translate( "Lieferung an die Rechnungsadresse" )}</div>
                </div>
              </div>
              <div className="opc-addr-cards-grid">
                {locations.map( ( loc, i ) => (
                  <AddressCard
                    key={i}
                    location={loc}
                    selected={locationSelected === loc.mapid}
                    onSelect={handleSelect}
                  />
                ) )}
              </div>
            </Modal.Content>
            <Modal.Actions>
              <Button primary size="large" onClick={() => setOpen( false )}>
                <Icon name="check"/> {translate( "Adresse bestätigen" )}
              </Button>
            </Modal.Actions>
          </Modal>
        </Fragment>
      )}
    </div>
  )
}

// ── Erfolgsseite (inline nach Bestellung) ───────────────────────────────────

const SuccessView = ( { bill, byId } ) => {
  if ( !bill ) return null
  const isOffer = bill.extras[ "ts-ordertype" ] === "offer"

  return (
    <div className="ui container mw9 ph3 center">
      {/* Hero */}
      <div className="opc-success__hero">
        <div className="opc-success__icon">
          <Icon name="check"/>
        </div>
        <h1 className="opc-success__title">
          {translate( "confirmation.title" )}{isOffer && ` ${translate( "Ihrer Anfrage" )}`}
        </h1>
        {shop.session.contact && (
          <p className="opc-success__subtitle">
            {isOffer
              ? translate( "confirmation.thanks_request_anonymous" )
              : translate( "confirmation.thanks_order_anonymous" )}
          </p>
        )}
      </div>

      <div className="opc-success__body">
        {/* Kundendaten */}
        <SectionCard>
          <h4 className="opc-section-title">{translate( "your data" )}</h4>
          <table className="opc-data-table">
            <colgroup><col width="35%"/><col/></colgroup>
            <tbody>
              {shop.config.checkoutFeatures && shop.config.checkoutFeatures.orderNumber && (
                <tr>
                  <td>{translate( "Order Id" )}</td>
                  <td>{bill.handid}</td>
                </tr>
              )}
              {bill.extras.manual_order_id && (
                <tr>
                  <td>{translate( "manual_order_id" )}</td>
                  <td>{bill.extras.manual_order_id}</td>
                </tr>
              )}
              {shop.config.checkoutFeatures && shop.config.checkoutFeatures.orderDate && (
                <tr>
                  <td>{translate( "Bestellzeitpunkt" )}</td>
                  <td>{moment( bill.created ).format( "HH:mm [Uhr], DD.MMM YYYY" )}</td>
                </tr>
              )}
              <tr>
                <td>{translate( "Kundennummer" )}</td>
                <td>{bill.customerid}</td>
              </tr>
              <tr>
                <td>{translate( "E-Mail" )}</td>
                <td>{bill.customer.email || bill.contact.email || "-"}</td>
              </tr>
            </tbody>
          </table>
        </SectionCard>

        {/* Adressen */}
        <SectionCard>
          <Row>
            <Col sm="1/2">
              <h4 className="opc-section-title">{translate( "Rechnungsadresse" )}</h4>
              <p className="opc-addr-text">
                {bill.bill.name1} {bill.bill.name2} {bill.bill.name3}<br/>
                {bill.bill.street}<br/>
                {bill.bill.pcode} {bill.bill.city}<br/>
                {bill.bill.country}
              </p>
            </Col>
            <Col sm="1/2">
              <h4 className="opc-section-title">{translate( "Lieferadresse" )}</h4>
              <p className="opc-addr-text">
                {bill.deliver.name1
                  ? <Fragment>{bill.deliver.name1} {bill.deliver.name2} {bill.deliver.name3}<br/>{bill.deliver.street}<br/>{bill.deliver.pcode} {bill.deliver.city}</Fragment>
                  : <Fragment>{bill.bill.name1} {bill.bill.name2}<br/>{bill.bill.street}<br/>{bill.bill.pcode} {bill.bill.city}</Fragment>
                }
              </p>
            </Col>
          </Row>
        </SectionCard>

        {/* Bestellpositionen */}
        <SectionCard>
          <h4 className="opc-section-title">
            {isOffer ? translate( "Ihre Anfrage" ) : translate( "Ihre Bestellung" )}
          </h4>
          <SuccessTable
            showTax={bill.extras.payment}
            positions={bill.positions}
            byId={byId}
            netto={bill.netto}
            currency={bill.currency}
          />
          {bill.comment && (
            <div className="opc-comment-block">
              <strong>{translate( "your comment" )}</strong>
              <p>{bill.comment}</p>
            </div>
          )}
          {!bill.extras.payment && (
            <div className="opc-notice-block">
              <p>{translate( "confirmation.notice" )}</p>
              <p>{translate( "confirmation.notice2" )}</p>
            </div>
          )}
        </SectionCard>

        <div className="opc-success__back">
          <Link to="/">
            <Button primary className="opc-btn-pill">
              <span className="opc-btn-icon-text">
                <Icon name="chevron left"/>{translate( "zurück zum Shop" )}
              </span>
            </Button>
          </Link>
        </div>
      </div>
    </div>
  )
}

// ── Hauptkomponente ─────────────────────────────────────────────────────────

@observer
export default class OnePageCheckoutPage extends Component {

  state = {
    products        : getProducts(),
    locations       : [],
    locationSelected: null,
    didPreselect    : false,
    comment         : shop.cart.data.comment || "",
    agb             : false,
    privacy         : false,
    submitting      : false,
    submitted       : false,
    bill            : null,
    byId            : {},
  }

  componentDidMount() {
    shop.cart.hooks.changed.add( this.updateCart )
    shop.cart.hydrate && shop.cart.hydrate()
    shop.cart.update && shop.cart.update()
    track.checkout( { step: 1, products: shop.cart.products } )
    shop.fetch( "/shop/rest/data/customers/current/addresses/deliver" )
      .then( locations => {
        if ( !locations || locations.length < 1 ) return
        this.setState( { locations } )
      } )
  }

  componentWillUnmount() {
    shop.cart.hooks.changed.remove( this.updateCart )
  }

  updateCart = () => {
    const newState = { products: getProducts() }
    if ( !this.state.didPreselect && shop.cart.data.deliveraddress && shop.cart.data.deliveraddress.mapid ) {
      newState.locationSelected = shop.cart.data.deliveraddress.mapid
      newState.didPreselect     = true
    }
    this.setState( newState )
    this.forceUpdate()
  }

  handleCartChange = ( { product } ) => shop.cart.add( product )

  handleLocationSelect = ( location ) => {
    if ( !location ) {
      this.setState( { locationSelected: null } )
      shop.cart.setDeliverAddress && shop.cart.setDeliverAddress( null )
      shop.cart.setAddress && shop.cart.setAddress( { location: null } )
      return
    }
    this.setState( { locationSelected: location.mapid } )
    shop.cart.setAddress( { location } )
  }

  handleCommentChange = ( e ) => {
    const comment = e.target.value
    this.setState( { comment } )
    shop.cart.setComment( { comment } )
  }

  handleOrderIdChange = ( e ) => {
    const manual_order_id = e.target.value
    this.setState( { manual_order_id } )
    shop.cart.setExtra( "manual_order_id", manual_order_id )
  }

  handleDateChange = ( date ) => {
    const m = moment( date )
    if ( m.isValid() ) {
      const deliverdate = m.format( "DD.MM.YYYY" )
      this.setState( { deliverdate } )
      shop.cart.setDeliverdate( { deliverdate } )
    } else {
      shop.cart.setDeliverdate( { deliverdate: "" } )
      this.setState( { deliverdate: "" } )
    }
  }

  doSubmit = ( isOffer ) => {
    const { agb, privacy } = this.state
    if ( !agb )     return shop.toast( translate( "please confirm our general terms" ), "error" )
    if ( !privacy ) return shop.toast( translate( "please confirm our privacy information" ), "error" )

    this.setState( { submitting: true } )
    shop.cart.setExtra( "is_quotation_request", isOffer )

    const extras = {
      delivery_address_id : shop.cart.data.deliveraddress && shop.cart.data.deliveraddress.mapid,
      manual_order_id     : shop.cart.getExtra( "manual_order_id" ),
      is_quotation_request: isOffer,
      attachments         : shop.cart.getExtra( "attachments" ),
      "ts-ordertype"      : isOffer ? "offer" : null,
      print_receipt       : shop.cart.getExtra( "print_receipt" ),
    }
    _.map( shop.cart.data.extras, ( v, k ) => { if ( !extras[ k ] ) extras[ k ] = v } )

    shop.cart.submit( { extras } )
      .then( bill => {
        this.setState( { bill, submitting: false, submitted: true } )
        shop.cart.productsById.clear()
        shop.cart.clear()
        shop.cart.update()
        return Promise.all( bill.positions.map( p => productStore.get( p.article_id ) ) )
      } )
      .then( products => {
        const byId = {}
        products.forEach( p => { byId[ p.map_id ] = p } )
        this.setState( { byId } )
      } )
      .catch( error => {
        console.error( "Checkout Error:", error )
        this.setState( { submitting: false } )
        swal( translate( "Error" ), String( error ), "error" )
      } )
  }

  render() {
    const {
      products, locations, locationSelected, comment,
      agb, privacy, submitting, submitted, bill, byId
    } = this.state

    const { referer = "/" } = queryString.parse( location.search, { ignoreQueryPrefix: true } )
    const { checkoutType }  = this.props.match.params
    const isRequest         = !!checkoutType

    const billingAddress       = shop.session.value || {}
    const { deliveraddress, deliverdate } = shop.cart.data
    const manual_order_id      = shop.cart.data.extras && shop.cart.data.extras.manual_order_id
    const checkoutFeatures     = shop.config.checkoutFeatures || {}
    const pluginFeatures       = shop.config.plugin || {}

    const tax       = shop.cart.sum / 100 * Number( shop.config.taxRate )
    const hasTax    = shop.config.taxRate
    const MIN_ORDER = shop.config.MIN_ORDER_VALUE || 0
    const canOrder  = agb && privacy && shop.cart.sum > MIN_ORDER

    if ( submitted && bill ) return <SuccessView bill={bill} byId={byId}/>

    if ( submitting ) {
      return (
        <div className="opc-spinner-wrap">
          <Spinner size="md" type="inverted"/>
          <p>{translate( "Ihre Bestellung wird verarbeitet..." )}</p>
        </div>
      )
    }

    return (
      <div className="OnePageCheckout">

        {/* ─── Schlanker Header ───────────────────────────────── */}
        <div className="opc-header">
          <Responsive minWidth={600}>
            <div className="container opc-header__inner">
              <Link to={referer}>
                <img src={shop.config.logo} alt="" className="opc-header__logo"/>
              </Link>
              <h1 className="opc-header__title">
                {isRequest ? translate( "Anfrage" ) : translate( "Checkout" )}
              </h1>
              <Link to={referer} className="opc-header__back-link">
                <Button basic className="opc-btn-round">
                  <Icon name="arrow left"/> {translate( "Zurück zum Shop" )}
                </Button>
              </Link>
            </div>
          </Responsive>
          <Responsive maxWidth={599}>
            <div className="container opc-header__inner">
              <Link to={referer} className="opc-header__back-link">
                <Button basic className="opc-btn-round">
                  <Icon name="arrow left"/> {translate( "Zurück" )}
                </Button>
              </Link>
              <h1 className="opc-header__title">
                {isRequest ? translate( "Anfrage" ) : translate( "Checkout" )}
              </h1>
              <div className="opc-header__spacer"/>
            </div>
          </Responsive>
        </div>

        {/* ─── Desktop: Zwei Spalten / Mobile: eine Spalte ───── */}
        <div className="container opc-body">
          <div className="opc-layout">

            {/* ── Linke Hauptspalte ──────────────────────────── */}
            <div className="opc-main">

              {/* ═══ SCHRITT 1: WARENKORB ═══════════════════════ */}
              <SectionCard>
                <StepHeader number="1" title={translate( "cart.cart" )}/>
                <ProductTable
                  readOnly={false}
                  cart={shop.cart}
                  onChange={this.handleCartChange}
                  products={products}
                />

                {shop.cart.sum <= MIN_ORDER && MIN_ORDER > 0 && (
                  <Message negative style={{ marginTop: "16px" }}>
                    <Icon name="info circle"/>
                    {translate( "minimum order value not reached" )}&ensp;
                    <strong><Price>{MIN_ORDER}</Price></strong>
                  </Message>
                )}
              </SectionCard>

              {/* ═══ SCHRITT 2: LIEFERUNG ════════════════════════ */}
              <SectionCard>
                <StepHeader number="2" title={translate( "Lieferung" )}/>

                {/* Rechnungs- und Lieferadresse nebeneinander */}
                <Responsive minWidth={600}>
                  <div className="opc-addresses">
                    <AddressBox
                      title={translate( "Rechnungsadresse" )}
                      address={billingAddress}
                      fixed
                    />
                    <AddressBox
                      title={translate( "Lieferadresse" )}
                      address={deliveraddress && deliveraddress.mapid ? deliveraddress : null}
                      fallback={translate( "Entspricht der Rechnungsadresse" )}
                      locations={locations}
                      locationSelected={locationSelected}
                      onSelect={this.handleLocationSelect}
                    />
                  </div>
                </Responsive>
                <Responsive maxWidth={599}>
                  <div className="opc-addresses opc-addresses--mobile">
                    <AddressBox
                      title={translate( "Rechnungsadresse" )}
                      address={billingAddress}
                      fixed
                    />
                    <AddressBox
                      title={translate( "Lieferadresse" )}
                      address={deliveraddress && deliveraddress.mapid ? deliveraddress : null}
                      fallback={translate( "Entspricht der Rechnungsadresse" )}
                      locations={locations}
                      locationSelected={locationSelected}
                      onSelect={this.handleLocationSelect}
                    />
                  </div>
                </Responsive>

                {/* Kommentar */}
                <FormField label={translate( "comment" )} htmlFor="opc-comment">
                  <FormInput
                    id="opc-comment"
                    placeholder={translate( "commentPlaceholder" )}
                    style={{ borderRadius: "8px", minHeight: "100px" }}
                    value={comment}
                    onChange={this.handleCommentChange}
                    multiline
                  />
                </FormField>

                {/* Bestellnummer */}
                {checkoutFeatures.manualOrderId && (
                  <div className="opc-field-narrow">
                    <FormField label={translate( "order number" )}>
                      <FormInput
                        placeholder={translate( "order number" )}
                        value={manual_order_id}
                        onChange={this.handleOrderIdChange}
                      />
                    </FormField>
                  </div>
                )}

                {/* Wunschlieferdatum */}
                {(checkoutFeatures.deliverdate || pluginFeatures.deliverdate) && (
                  <div className="opc-field-narrow">
                    <FormField label={translate( "delivery after" )}>
                      <DeliveryDatePicker date={deliverdate} onChange={this.handleDateChange}/>
                    </FormField>
                  </div>
                )}

                {/* Vertriebsfelder */}
                {(shop.session.handId && !shop.config.hideAddtionalCheckoutMetaForPowerUsers) && (
                  <div className="opc-backoffice">
                    <Divider horizontal style={{ color: "#aaa", fontSize: "0.8rem" }}>
                      {translate( "back office" )}
                    </Divider>
                    <div className="opc-field-narrow">
                      <FormField label={translate( "Belegausdruck/Innendienst" )}>
                        <FormInput
                          placeholder={translate( "Belegausdruck/Innendienst" )}
                          value={shop.cart.data.print_receipt}
                          onChange={e => shop.cart.setExtra( "print_receipt", e.target.value )}
                          multiline
                        />
                      </FormField>
                    </div>
                  </div>
                )}
              </SectionCard>

              {/* ═══ SCHRITT 3: ABSCHLUSS ════════════════════════ */}
              <SectionCard>
                <StepHeader number="3" title={translate( "Abschluss" )}/>

                <Form type="horizontal">
                  {/* AGB */}
                  <div className={`opc-consent${agb ? " opc-consent--accepted" : ""}`}>
                    <div className="opc-consent__row">
                      <Checkbox
                        value={agb}
                        onChange={e => this.setState( { agb: e.target.checked } )}
                        label={translate( "I accept the general terms and conditions" )}
                      />
                      <Link target="_blank" to="/cms/agb" className="opc-consent__link">
                        {translate( "AGB" )} ↗
                      </Link>
                    </div>
                  </div>

                  {/* Datenschutz */}
                  <div className={`opc-consent opc-consent--last${privacy ? " opc-consent--accepted" : ""}`}>
                    <div className="opc-consent__row">
                      <Checkbox
                        value={privacy}
                        onChange={e => this.setState( { privacy: e.target.checked } )}
                        label={translate( "privacy_notice_notice" )}
                      />
                      <Link target="_blank" to="/cms/datenschutz" className="opc-consent__link">
                        {translate( "Datenschutzerklärung" )} ↗
                      </Link>
                    </div>
                  </div>
                </Form>

                <Divider/>

                <div className="opc-footer-actions">
                  <Link to={referer}>
                    <Button basic className="opc-btn-round">
                      <Icon name="arrow left"/> {translate( "Zurück" )}
                    </Button>
                  </Link>

                  <div className="opc-footer-actions__right">
                    {!isRequest && (
                      <Button
                        primary
                        disabled={!canOrder}
                        loading={submitting}
                        onClick={() => this.doSubmit( false )}
                        className="opc-btn-pill"
                      >
                        <Icon name="check"/> {translate( "order now" )}
                      </Button>
                    )}
                    {isRequest && (
                      <Button
                        primary
                        disabled={!agb || !privacy}
                        loading={submitting}
                        onClick={() => this.doSubmit( true )}
                        className="opc-btn-pill"
                      >
                        {translate( "send enquiry" )}
                      </Button>
                    )}
                  </div>
                </div>
              </SectionCard>

            </div>{/* /Hauptspalte */}

            {/* ── Rechte Sidebar (sticky) ─────────────────────── */}
            <Responsive minWidth={900}>
              <div className="opc-sidebar">

                {/* Zusammenfassung */}
                <SectionCard>
                  <h3 className="opc-summary__title">
                    {translate( "Zusammenfassung" )}
                  </h3>

                  <div className="opc-summary__totals">
                    <div className="opc-summary__row">
                      <span>{shop.cart.size} {translate( "positions" )}</span>
                      <Price>{shop.cart.sum}</Price>
                    </div>

                    {shop.session.handId && !!shop.cart.sumDiscount && (
                      <Fragment>
                        <div className="opc-summary__row opc-summary__row--discount">
                          <span>{translate( "Discount" )}</span>
                          <span>− <Price>{shop.cart.sumDiscount}</Price></span>
                        </div>
                        <div className="opc-summary__row opc-summary__row--after-discount">
                          <span>{translate( "nach Rabatt" )}</span>
                          <Price>{shop.cart.sum - shop.cart.sumDiscount}</Price>
                        </div>
                      </Fragment>
                    )}

                    {hasTax && (
                      <div className="opc-summary__row opc-summary__row--tax">
                        <span>{translate( "zzgl. Steuern" )} ({shop.config.taxRate.replace( ".", "," )}%)</span>
                        <Price>{tax}</Price>
                      </div>
                    )}

                    <div className="opc-summary__total">
                      <span>{hasTax ? translate( "inkl. Steuern" ) : translate( "Gesamt" )}</span>
                      <Price>{hasTax ? tax + shop.cart.sum : shop.cart.sum}</Price>
                    </div>
                  </div>

                  {/* Lieferadresse Preview – Fallback auf Rechnungsadresse */}
                  {(() => {
                    const hasDeliverAddr = deliveraddress && deliveraddress.mapid
                    const addr = hasDeliverAddr ? deliveraddress : billingAddress
                    if ( !addr ) return null
                    return (
                      <div className="opc-deliver-preview">
                        <div className="opc-deliver-preview__label">
                          <Icon name="truck"/> {translate( "Lieferung an:" )}
                        </div>
                        <div>{addr.name1} {addr.name2}</div>
                        <div>{addr.street}</div>
                        <div>{addr.pcode} {addr.city}</div>
                        {!hasDeliverAddr && (
                          <div className="opc-deliver-preview__fallback">
                            ({translate( "Rechnungsadresse" )})
                          </div>
                        )}
                      </div>
                    )
                  })()}

                  {/* CTA */}
                  <div className="opc-cta">
                    {!isRequest && (
                      <Button
                        primary fluid size="large"
                        disabled={!canOrder}
                        loading={submitting}
                        onClick={() => this.doSubmit( false )}
                        className="opc-btn-pill"
                      >
                        <Icon name="check"/> {translate( "order now" )}
                      </Button>
                    )}
                    {isRequest && (
                      <Button
                        primary fluid size="large"
                        disabled={!agb || !privacy}
                        loading={submitting}
                        onClick={() => this.doSubmit( true )}
                        className="opc-btn-pill"
                      >
                        {translate( "send enquiry" )}
                      </Button>
                    )}

                    {(!agb || !privacy) && (
                      <p className="opc-hint">
                        <Icon name="info circle"/>{" "}
                        {translate( "Bitte bestätigen Sie AGB und Datenschutz" )}
                      </p>
                    )}

                    {agb && privacy && shop.cart.sum <= MIN_ORDER && MIN_ORDER > 0 && (
                      <Message negative size="small" style={{ marginTop: "10px", fontSize: "0.82rem" }}>
                        <Icon name="info circle"/> {translate( "minimum order value not reached" )}
                      </Message>
                    )}
                  </div>
                </SectionCard>

              </div>
            </Responsive>

          </div>
        </div>

      </div>
    )
  }
}

const getProducts = () => shop.cart.map( product => product )



// WEBPACK FOOTER //
// ./src/theme/demo2025/pages/checkout/OnePageCheckoutPage.js